import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { clsx } from 'clsx'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = ({
  children,
  ...props
}: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    <div className="fixed inset-0 z-[110] flex items-start justify-center sm:items-center">
      {children}
    </div>
  </DialogPrimitive.Portal>
)
DialogPortal.displayName = DialogPrimitive.Portal.displayName

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={clsx(
      'fixed inset-0 z-50 bg-dark-base opacity-80 backdrop-blur-sm transition-all duration-100 radix-state-closed:animate-fade-out radix-state-open:animate-overlay',
      className
    )}
    onContextMenu={(e) => {
      e.stopPropagation()
    }}
    {...props}
    ref={ref}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close className={clsx('', className)} {...props} ref={ref} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    overlayProps?: React.ComponentPropsWithoutRef<
      typeof DialogPrimitive.Overlay
    >
  }
>(({ className, children, overlayProps, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay {...overlayProps} />
    <DialogPrimitive.Content
      ref={ref}
      className={clsx(
        'fixed top-1/2 z-50 w-full max-w-sm !-translate-y-1/2 shadow-modal transform rounded-2xl border border-solid border-dark-border-solid bg-dark-base radix-state-closed:animate-fade-out radix-state-open:animate-dialog sm:max-w-[460px] py-5 px-6 max-h-[90vh] overflow-y-auto focus:outline-none',
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  divider,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  divider?: boolean
}) => (
  <>
    <div
      className={clsx('flex flex-col space-y-1 mb-5', className)}
      {...props}
    />
    {divider && <DialogDivider className="!mt-4" />}
  </>
)
DialogHeader.displayName = 'DialogHeader'

export const DialogDivider = ({ className }: { className?: string }) => {
  return (
    <hr className={clsx(className, 'my-5 -mx-6 border-dark-border-solid')} />
  )
}

const DialogFooter = ({
  className,
  children,
  divider,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  divider?: boolean
}) => (
  <>
    {divider && <DialogDivider />}
    <div
      className={clsx(
        'flex flex-col-reverse sm:flex-row sm:justify-end gap-2',
        className
      )}
      {...props}
    >
      {children}
    </div>
  </>
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={clsx('font-medium text-lg text-pretty', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={clsx('leading-[1.4] text-dark-muted text-pretty', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
}
